import { authClient } from '@/providers/auth.provider'
import { req } from '@/providers/axios.provider'
import { useStorePersonalize } from '@/stores/personalize.store'
import { resetAllStores } from '@/stores/reset.store'
import { clearAnalyticsContext, clearGa4UserId, track } from '@/utils/analytics'
import { clearClarityUser } from '@/utils/clarity.util'
import { clearUser as clearMonitoringUser } from '@/utils/monitoring'

import type { BookStatusT } from './users.api'

export type BookTypeT = 'memoir' | 'self-growth' | 'loved' | 'faith' | 'personalized'

/**
 * Which identity a request resolves to. `writer` is the only role that acts as *themselves*
 * rather than being rewritten to the account owner — see the Family Circle handoff.
 */
export type AccountRoleT = 'solo' | 'owner' | 'editor' | 'viewer' | 'writer'

export type ChangelogEntryT = {
  id: string
  version: string
  title: string
  body: string
}

export type UserT = {
  id: string
  name: string
  email: string
  emailVerified: boolean
  image?: string
  lastVisit: string | null
  isFrozen: boolean
  isOnboarded: boolean
  bookStatus: BookStatusT | null
  country: string | null
  ip: string | null
  locale: string | null
  hasSeenNpsAfterFirstAnswer: boolean
  paymentDeclinedAt: string | null
  createdAt: string
  updatedAt: string
  orderBookCount: number
  isTest: boolean
  isGift: boolean
  hasFamilyCircle: boolean
  hasCollaborators: boolean
  /**
   * @deprecated Derived from "has any organization_members row", so it is now true for family
   * writers as well as collaborators and can no longer tell them apart. Branch on `accountRole`.
   */
  isInvited: boolean
  /** Single source of truth for navigation and feature gating. */
  accountRole: AccountRoleT
  /** Convenience for `accountRole === 'writer'`. */
  isFamilyWriter: boolean
  /** Whose circle the writer is in. Null for every other role. */
  familyOwnerName: string | null
  externalInfo: {
    code?: string
    purchasedBookType?: BookTypeT
    isMigrated?: boolean
    language?: string
    writerName?: string
  } | null
  unseenChangelog?: ChangelogEntryT[]
  showRenewalRewardModal?: boolean
  settings?: {
    questionLibraryReleaseAt: string
    bookInteriorReleaseAt?: string
  }
}

// ------------------------------------------------------------------

export const API_POST_SignInWithGoogle = async () => {
  const { data, error } = await authClient.signIn.social({
    provider: 'google',
    callbackURL: `${window.location.origin}/dashboard/my-books`,
  })
  return { data, error: error?.message }
}

// ------------------------------------------------------------------

export const API_POST_SignOut = async () => {
  const { error } = await authClient.signOut()
  if (error) return { error: error.message }

  track('logout')
  clearGa4UserId()
  clearAnalyticsContext()
  clearClarityUser()
  clearMonitoringUser()

  // The session cookie is gone, but every store still holds the previous user's data and
  // would leak into the next sign-in on this tab, so wipe them before leaving the dashboard.
  resetAllStores()
  useStorePersonalize.getState().reset()
  useStorePersonalize.persist.clearStorage()

  return { error: undefined }
}

// ------------------------------------------------------------------

export const API_PATCH_UpdateUser = async (body: {
  name?: string
  image?: string
  lastVisit?: string
  locale?: string
  seenChangelogEntryId?: string
}): Promise<UserT> => {
  const res = await req.patch<UserT>(`/api/users/me`, body)
  return res
}

// ------------------------------------------------------------------

export const API_POST_SignInWithMagicLink = async (email: string) => {
  const res = await req.post('/api/auth/sign-in/magic-link', {
    email: email,
    callbackURL: `${window.location.origin}/dashboard/my-books`,
    newUserCallbackURL: `${window.location.origin}/dashboard/my-books`,
  })
  return res
}

export const API_GET_RedeemInviteSignIn = async (token: string): Promise<boolean> => {
  try {
    await req.get('/api/auth/magic-link/verify', { params: { token }, skipErrorToast: true })
    return true
  } catch {
    return false
  }
}
